import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import statusDefinitionService from '../../services/statusDefinitionService';

export const fetchStatusDefinitions = createAsyncThunk(
  'statusDefinitions/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await statusDefinitionService.getAllGrouped();
      return data.data.statusDefinitions;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

const statusDefinitionsSlice = createSlice({
  name: 'statusDefinitions',
  initialState: {
    byDomain: {},
    loaded: false,
    loading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStatusDefinitions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStatusDefinitions.fulfilled, (state, action) => {
        state.byDomain = action.payload;
        state.loaded = true;
        state.loading = false;
      })
      .addCase(fetchStatusDefinitions.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Statuses for a single domain, ordered.
export const selectStatusesByDomain = (domain) => (state) =>
  state.statusDefinitions.byDomain[domain] || [];

// [{value, label}] for a domain, ordered — for <MenuItem>/<Tab> lists.
export const selectStatusOptions = (domain) => (state) =>
  (state.statusDefinitions.byDomain[domain] || []).map((s) => ({ value: s.key, label: s.label }));

// Ordered array of keys for a domain — for progress trackers / "next status" logic.
export const selectStatusOrder = (domain) => (state) =>
  (state.statusDefinitions.byDomain[domain] || []).map((s) => s.key);

// { key: label } for a domain.
export const selectStatusLabels = (domain) => (state) => {
  const labels = {};
  (state.statusDefinitions.byDomain[domain] || []).forEach((s) => { labels[s.key] = s.label; });
  return labels;
};

// { key: {label,color,bg} } for ONE domain — the correct source for any
// status Chip, since the same key can exist in multiple domains with
// different colors (e.g. "pending" in item/deliveryReceipt/payment, or
// "delivered" in shipment/deliveryReceipt/vehicle).
export const selectStatusChipConfigForDomain = (domain) => (state) => {
  const config = {};
  (state.statusDefinitions.byDomain[domain] || []).forEach((s) => {
    config[s.key] = { label: s.label, color: s.color, bg: s.bg };
  });
  return config;
};

// Flat { key: {label,color,bg} } merged across every domain — LAST domain
// in DOMAINS order wins on key collisions, so this is only a reasonable
// choice when the domain genuinely isn't known at the call site. Prefer
// selectStatusChipConfigForDomain(domain) wherever the domain is known.
export const selectStatusChipConfig = (state) => {
  const config = {};
  Object.values(state.statusDefinitions.byDomain).forEach((statuses) => {
    statuses.forEach((s) => {
      config[s.key] = { label: s.label, color: s.color, bg: s.bg };
    });
  });
  return config;
};

// The status key currently holding a given automated-behavior role in a
// domain (e.g. selectKeyForRole('invoice','paid')) — mirrors the backend's
// getKeyForRole, so frontend components can match "which status means X"
// without hardcoding the key, since role data is already on every status doc.
export const selectKeyForRole = (domain, role) => (state) => {
  const statuses = state.statusDefinitions.byDomain[domain] || [];
  return statuses.find((s) => (s.roles || []).includes(role))?.key || null;
};

export default statusDefinitionsSlice.reducer;
